import logger from 'utils/log';
import { getStorageItem, setStorageItem } from 'utils/ui/storage';

export interface BandwidthConfig {
  gracePeriodMs: number;
  maxConcurrentStreams: number;
  rapidSwitchingThresholdMs: number;
  heartbeatIntervalMs: number;
  deadStreamTimeoutMs: number;
}

export interface StreamInstance {
  id: string;
  tabId: string;
  isActive: boolean;
  lastActiveTime: number;
  lastHeartbeat: number;
}

export interface BandwidthManagerEvents {
  onStreamStateChange: (streamId: string, shouldBeActive: boolean) => void;
  onError: (error: Error) => void;
}

export class BandwidthManager {
  private static instance: BandwidthManager;
  private config: BandwidthConfig;
  private streams = new Map<string, StreamInstance>();
  private timers = new Map<string, NodeJS.Timeout>();
  private events: BandwidthManagerEvents;
  private broadcastChannel: BroadcastChannel;
  private isTabVisible = true;
  private lastVisibilityChange = 0;
  private heartbeatInterval?: NodeJS.Timeout;
  private deadStreamCleanupInterval?: NodeJS.Timeout;
  private isChannelClosed = false;

  private constructor(config: BandwidthConfig, events: BandwidthManagerEvents) {
    this.config = config;
    this.events = events;
    this.broadcastChannel = new BroadcastChannel('bandwidth-manager');
    this.setupEventListeners();
  }

  public static getInstance(config?: BandwidthConfig, events?: BandwidthManagerEvents): BandwidthManager {
    if (!BandwidthManager.instance) {
      if (!config || !events) {
        throw new Error('BandwidthManager must be initialized with config and events');
      }
      BandwidthManager.instance = new BandwidthManager(config, events);
    }
    return BandwidthManager.instance;
  }

  private setupEventListeners(): void {
    // Page Visibility API
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Window focus events as fallback
    window.addEventListener('focus', this.handleWindowFocus.bind(this));
    window.addEventListener('blur', this.handleWindowBlur.bind(this));

    // Cross-tab communication
    this.broadcastChannel.addEventListener('message', this.handleBroadcastMessage.bind(this));

    // Cleanup on beforeunload
    window.addEventListener('beforeunload', this.cleanup.bind(this));

    // Start heartbeat and cleanup systems
    this.startHeartbeat();
    this.startDeadStreamCleanup();
  }

  private handleVisibilityChange(): void {
    const now = Date.now();
    const previousVisibilityChange = this.lastVisibilityChange;
    this.isTabVisible = !document.hidden;
    this.lastVisibilityChange = now;

    const isRapidSwitching = now - previousVisibilityChange < this.config.rapidSwitchingThresholdMs;

    if (this.isTabVisible) {
      logger.debug('[BANDWIDTH] Tab became active', {
        isRapidSwitching,
        timeSinceLastChange: now - previousVisibilityChange,
      });
      this.handleTabActive(isRapidSwitching);
    } else {
      logger.debug('[BANDWIDTH] Tab became inactive', {
        isRapidSwitching,
        gracePeriod: this.config.gracePeriodMs,
      });
      this.handleTabInactive(isRapidSwitching);
    }
  }

  private handleWindowFocus(): void {
    if (this.isTabVisible) return; // Visibility API takes precedence
    this.handleTabActive(false);
  }

  private handleWindowBlur(): void {
    if (this.isTabVisible) return; // Visibility API takes precedence
    this.handleTabInactive(false);
  }

  private handleTabActive(isRapidSwitching: boolean): void {
    this.streams.forEach((stream, streamId) => {
      if (stream.tabId === this.getTabId()) {
        this.clearTimer(streamId);

        logger.log('[BANDWIDTH] Activating stream for active tab', {
          streamId,
          isRapidSwitching,
          wasInactive: !stream.isActive,
        });
        this.activateStream(streamId);
      }
    });
  }

  private handleTabInactive(isRapidSwitching: boolean): void {
    this.streams.forEach((stream, streamId) => {
      if (stream.tabId === this.getTabId() && stream.isActive) {
        if (isRapidSwitching) {
          logger.debug('[BANDWIDTH] Skipping deactivation due to rapid switching', { streamId });
          return;
        }

        logger.log('[BANDWIDTH] Starting grace period for stream', {
          streamId,
          gracePeriod: this.config.gracePeriodMs,
        });

        // Set timer for deactivation
        const timer = setTimeout(() => {
          logger.log('[BANDWIDTH] Grace period expired, deactivating stream', { streamId });
          this.deactivateStream(streamId);
        }, this.config.gracePeriodMs);

        this.timers.set(streamId, timer);
      }
    });
  }

  private handleBroadcastMessage(event: MessageEvent): void {
    const { type, data } = event.data;

    logger.debug('[BANDWIDTH] Received broadcast message', { type, data });

    switch (type) {
      case 'stream-registered':
        this.handleRemoteStreamRegistered(data);
        break;
      case 'stream-unregistered':
        this.handleRemoteStreamUnregistered(data);
        break;
      case 'stream-state-changed':
        this.handleRemoteStreamStateChanged(data);
        break;
      case 'stream-states':
        this.handleRemoteStreamStates(data);
        break;
      case 'request-stream-states':
        this.broadcastStreamStates();
        break;
      case 'heartbeat':
        this.handleRemoteHeartbeat(data);
        break;
      default:
        logger.warn('[BANDWIDTH] Unknown broadcast message type', { type });
    }
  }

  private handleRemoteStreamRegistered(data: { streamId: string; tabId: string }): void {
    if (!this.streams.has(data.streamId)) {
      this.streams.set(data.streamId, {
        id: data.streamId,
        tabId: data.tabId,
        isActive: false,
        lastActiveTime: 0,
        lastHeartbeat: Date.now(),
      });
    }
  }

  private handleRemoteStreamStateChanged(data: { streamId: string; isActive: boolean; lastActiveTime: number }): void {
    const stream = this.streams.get(data.streamId);
    if (stream && stream.tabId !== this.getTabId()) {
      logger.debug('[BANDWIDTH] Remote stream state changed', {
        streamId: data.streamId,
        isActive: data.isActive,
        wasActive: stream.isActive,
      });
      stream.isActive = data.isActive;
      stream.lastActiveTime = data.lastActiveTime;
      this.enforceStreamLimits();
    }
  }

  private handleRemoteStreamUnregistered(data: { streamId: string }): void {
    const stream = this.streams.get(data.streamId);
    if (stream && stream.tabId !== this.getTabId()) {
      logger.debug('[BANDWIDTH] Remote stream unregistered', { streamId: data.streamId });
      this.streams.delete(data.streamId);
    }
  }

  private handleRemoteStreamStates(data: {
    streams: Array<{ streamId: string; isActive: boolean; lastActiveTime: number; lastHeartbeat?: number }>;
  }): void {
    logger.debug('[BANDWIDTH] Received remote stream states', { streams: data.streams, count: data.streams.length });
    data.streams.forEach((remoteStream) => {
      const existingStream = this.streams.get(remoteStream.streamId);
      if (existingStream && existingStream.tabId !== this.getTabId()) {
        existingStream.isActive = remoteStream.isActive;
        existingStream.lastActiveTime = remoteStream.lastActiveTime;
        existingStream.lastHeartbeat = remoteStream.lastHeartbeat || Date.now();
      }
    });

    this.enforceStreamLimits();
  }

  private handleRemoteHeartbeat(data: { tabId: string; streamIds: string[]; timestamp: number }): void {
    // Update heartbeat for streams from this tab
    data.streamIds.forEach((streamId) => {
      const stream = this.streams.get(streamId);
      if (stream && stream.tabId === data.tabId) {
        stream.lastHeartbeat = data.timestamp;
      }
    });
  }

  public registerStream(streamId: string): void {
    // Prevent duplicate registrations
    if (this.streams.has(streamId)) {
      logger.warn('[BANDWIDTH] Stream already registered, skipping', { streamId });
      return;
    }

    const tabId = this.getTabId();
    const now = Date.now();

    this.streams.set(streamId, {
      id: streamId,
      tabId,
      isActive: this.isTabVisible,
      lastActiveTime: now,
      lastHeartbeat: now,
    });

    logger.log('[BANDWIDTH] Stream registered', {
      streamId,
      tabId,
      initiallyActive: this.isTabVisible,
      totalStreams: this.streams.size,
    });

    // Broadcast registration to other tabs
    if (!this.isChannelClosed) {
      this.broadcastChannel.postMessage({
        type: 'stream-registered',
        data: { streamId, tabId },
      });

      // Request current states from other tabs
      this.broadcastChannel.postMessage({ type: 'request-stream-states' });
    }

    // Clean up dead streams before enforcing limits
    this.cleanupDeadStreams();

    // Enforce limits after registration
    setTimeout(() => this.enforceStreamLimits(), 100);
  }

  public unregisterStream(streamId: string): void {
    if (!this.streams.has(streamId)) {
      logger.warn('[BANDWIDTH] Attempting to unregister non-existent stream', { streamId });
      return;
    }

    this.clearTimer(streamId);
    this.streams.delete(streamId);

    logger.log('[BANDWIDTH] Stream unregistered', {
      streamId,
      remainingStreams: this.streams.size,
    });

    if (!this.isChannelClosed) {
      this.broadcastChannel.postMessage({
        type: 'stream-unregistered',
        data: { streamId },
      });
    }
  }

  public updateConfig(newConfig: Partial<BandwidthConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  private activateStream(streamId: string): void {
    const stream = this.streams.get(streamId);
    if (!stream || stream.tabId !== this.getTabId()) return;

    if (!this.canActivateStream()) {
      logger.warn('[BANDWIDTH] Cannot activate stream, enforcing limits', {
        streamId,
        maxAllowed: this.config.maxConcurrentStreams,
      });
      this.enforceStreamLimits();
      return;
    }

    stream.isActive = true;
    stream.lastActiveTime = Date.now();

    try {
      logger.log('[BANDWIDTH] Stream activated successfully', {
        streamId,
      });
      this.events.onStreamStateChange(streamId, true);
      this.broadcastStreamStateChange(streamId, true);
    } catch (error) {
      logger.error('[BANDWIDTH] Failed to activate stream', { streamId, error });
      this.events.onError(error as Error);
      stream.isActive = false;
    }
  }

  private deactivateStream(streamId: string): void {
    const stream = this.streams.get(streamId);
    if (!stream || stream.tabId !== this.getTabId() || !stream.isActive) return;

    stream.isActive = false;

    try {
      logger.log('[BANDWIDTH] Stream deactivated', {
        streamId,
      });
      this.events.onStreamStateChange(streamId, false);
      this.broadcastStreamStateChange(streamId, false);
    } catch (error) {
      logger.error('[BANDWIDTH] Failed to deactivate stream', { streamId, error });
      this.events.onError(error as Error);
    }
  }

  private canActivateStream(): boolean {
    const activeStreams = Array.from(this.streams.values()).filter((s) => s.isActive);

    // Always allow if under limit
    if (activeStreams.length < this.config.maxConcurrentStreams) {
      return true;
    }

    // Can replace oldest stream from a different tab
    const oldestActiveFromOtherTab = activeStreams
      .filter((s) => s.tabId !== this.getTabId())
      .sort((a, b) => a.lastActiveTime - b.lastActiveTime)[0];

    return !!oldestActiveFromOtherTab;
  }

  private enforceStreamLimits(): void {
    const activeStreams = Array.from(this.streams.values())
      .filter((s) => s.isActive)
      .sort((a, b) => b.lastActiveTime - a.lastActiveTime);

    if (activeStreams.length <= this.config.maxConcurrentStreams) {
      return;
    }

    // Deactivate oldest streams from other tabs first
    const streamsToDeactivate = activeStreams
      .slice(this.config.maxConcurrentStreams)
      .filter((s) => s.tabId !== this.getTabId());

    streamsToDeactivate.forEach((stream) => {
      if (stream.tabId === this.getTabId()) {
        this.deactivateStream(stream.id);
      }
    });
  }

  private clearTimer(streamId: string): void {
    const timer = this.timers.get(streamId);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(streamId);
    }
  }

  private broadcastStreamStateChange(streamId: string, isActive: boolean): void {
    if (this.isChannelClosed) return;

    const stream = this.streams.get(streamId);
    if (stream) {
      this.broadcastChannel.postMessage({
        type: 'stream-state-changed',
        data: {
          streamId,
          isActive,
          lastActiveTime: stream.lastActiveTime,
        },
      });
    }
  }

  private broadcastStreamStates(): void {
    if (this.isChannelClosed) return;

    const localStreams = Array.from(this.streams.values())
      .filter((s) => s.tabId === this.getTabId())
      .map((s) => ({
        streamId: s.id,
        isActive: s.isActive,
        lastActiveTime: s.lastActiveTime,
        lastHeartbeat: s.lastHeartbeat,
      }));

    if (localStreams.length > 0) {
      this.broadcastChannel.postMessage({
        type: 'stream-states',
        data: { streams: localStreams },
      });
    }
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      if (this.isChannelClosed) return;

      const localStreamIds = Array.from(this.streams.values())
        .filter((s) => s.tabId === this.getTabId())
        .map((s) => s.id);

      if (localStreamIds.length > 0) {
        const now = Date.now();

        // Update heartbeat for local streams
        localStreamIds.forEach((streamId) => {
          const stream = this.streams.get(streamId);
          if (stream) {
            stream.lastHeartbeat = now;
          }
        });

        this.broadcastChannel.postMessage({
          type: 'heartbeat',
          data: {
            tabId: this.getTabId(),
            streamIds: localStreamIds,
            timestamp: now,
          },
        });
      }
    }, this.config.heartbeatIntervalMs);
  }

  private startDeadStreamCleanup(): void {
    this.deadStreamCleanupInterval = setInterval(() => {
      this.cleanupDeadStreams();
    }, this.config.heartbeatIntervalMs * 2);
  }

  private cleanupDeadStreams(): void {
    const now = Date.now();
    const deadStreams: string[] = [];

    this.streams.forEach((stream, streamId) => {
      // Only clean up streams from other tabs
      if (stream.tabId !== this.getTabId()) {
        const timeSinceHeartbeat = now - stream.lastHeartbeat;

        if (timeSinceHeartbeat > this.config.deadStreamTimeoutMs) {
          deadStreams.push(streamId);
        }
      }
    });

    if (deadStreams.length > 0) {
      logger.log('[BANDWIDTH] Cleaning up dead streams', { count: deadStreams.length, streamIds: deadStreams });

      deadStreams.forEach((streamId) => {
        this.streams.delete(streamId);
      });

      this.enforceStreamLimits();
    }
  }

  private getTabId(): string {
    if (!getStorageItem('sessionStorage', 'bandwidth-tab-id')) {
      setStorageItem('sessionStorage', 'bandwidth-tab-id', `tab-${crypto.randomUUID()}`);
    }
    return getStorageItem('sessionStorage', 'bandwidth-tab-id')!;
  }

  private cleanup(): void {
    this.isChannelClosed = true;

    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    if (this.deadStreamCleanupInterval) {
      clearInterval(this.deadStreamCleanupInterval);
    }

    this.broadcastChannel.close();
  }
}

export const DEFAULT_BANDWIDTH_CONFIG: BandwidthConfig = {
  gracePeriodMs: 30000, // 30 seconds
  maxConcurrentStreams: 2,
  rapidSwitchingThresholdMs: 1000, // 1 second
  heartbeatIntervalMs: 15000, // 15 seconds
  deadStreamTimeoutMs: 45000, // 45 seconds
};
